/**
 * The save state every manual editor shows in the panel header (plan 7.3):
 * "Saving…", then "Saved" with the existing `cloud_done` glyph, "Couldn't
 * save. Try again." on failure, the server's own words for a refused save
 * (422) or the hourly cap (429), and the conflict message with Reload.
 * Platform-free, so the web header, the native header and the tests read
 * the same thing.
 */
import type { SaveFailure } from './save-errors';

export type EditorSaveState =
  | { status: 'idle' }
  /** Changes waiting for the debounce. */
  | { status: 'dirty' }
  | { status: 'saving' }
  | { status: 'saved' }
  | { status: 'failed' }
  | { status: 'limited'; message: string | null }
  | { status: 'invalid'; message: string | null }
  | { status: 'conflict' };

export const IDLE_SAVE_STATE: EditorSaveState = Object.freeze({ status: 'idle' });

/** The state after a save failed for good (retries spent). */
export function saveStateForFailure(failure: SaveFailure): EditorSaveState {
  switch (failure.kind) {
    case 'conflict':
      return { status: 'conflict' };
    case 'invalid':
      return { status: 'invalid', message: failure.message };
    case 'limited':
      return { status: 'limited', message: failure.message };
    default:
      return { status: 'failed' };
  }
}

export type SaveStateView = {
  /** A copy key, or null when `text` (the server's words) is shown. */
  key: string | null;
  text: string | null;
  glyph: 'spinner' | 'saved' | 'error' | null;
  tone: 'neutral' | 'error';
  /** The button beside the words. */
  action: 'retry' | 'reload' | null;
  /** Announced politely (the header's live region). */
  announce: boolean;
};

export function saveStateView(state: EditorSaveState): SaveStateView {
  switch (state.status) {
    case 'dirty':
    case 'saving':
      return { key: 'chat.artifacts.edit.saving', text: null, glyph: 'spinner', tone: 'neutral', action: null, announce: false };
    case 'saved':
      return { key: 'chat.artifacts.edit.saved', text: null, glyph: 'saved', tone: 'neutral', action: null, announce: true };
    case 'failed':
      return { key: 'chat.artifacts.edit.saveFailed', text: null, glyph: 'error', tone: 'error', action: 'retry', announce: true };
    case 'limited':
    case 'invalid':
      return state.message
        ? { key: null, text: state.message, glyph: 'error', tone: 'error', action: state.status === 'limited' ? 'retry' : null, announce: true }
        : { key: 'chat.artifacts.edit.saveFailed', text: null, glyph: 'error', tone: 'error', action: 'retry', announce: true };
    case 'conflict':
      return { key: 'chat.artifacts.edit.conflict', text: null, glyph: 'error', tone: 'error', action: 'reload', announce: true };
    default:
      return { key: null, text: null, glyph: null, tone: 'neutral', action: null, announce: false };
  }
}

/** Whether leaving edit mode would lose changes (Done waits for the save). */
export function hasPendingSave(state: EditorSaveState): boolean {
  return state.status === 'dirty' || state.status === 'saving';
}
